// Salary TDS (section 192) — the monthly challan cycle and the quarterly 24Q
// cycle, as two steppers.
//
// Every "is this step done" answer is DERIVED from stored facts by the backend
// (/tds/quarter) rather than from a "done" flag: a flag can disagree with
// reality, a derivation cannot. This file only renders what it is told and
// offers the action that moves each step forward.
//
// Design: hrms_prod/docs/superpowers/specs/2026-09-30-salary-tds-24q-design.md
import StatutorySteps, { STEP_CSS } from "./StatutorySteps";
import type { StepState as StepState2 } from "./StatutorySteps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { inr, Notice } from "./reportsUi";
import { PayrollFilters } from "../../api/salaryReports";
import { incomeTaxSettingsApi } from "../../api/incomeTaxSettings";
import { sessionGet } from "../../utils/sessionStore";
import {
  pingItExtension,
  startFileTds,
  startItLogin,
  startReadPortalChallans,
  onItHistoryDone,
  startUpload24Q,
  // getItDebugLog,
  onItStatus,
  onItReady,
  onItError,
  // getItExtensionVersion,   // only named the build in the debug-log error
} from "../../utils/incomeTaxExtension";
import { showExtensionMissing } from "../../utils/extensionInstall";
// import ExtensionLogDialog, { type ExtensionLogLine } from "./ExtensionLogDialog";
import TdsTransactions from "./TdsTransactions";
import TdsDeductees from "./TdsDeductees";
import CreateChallanDialog from "./CreateChallanDialog";
import TdsFilterBar, { EMPTY_FILTERS, TdsFilters } from "./TdsFilterBar";
import TdsChallanData from "./TdsChallanData";
import PortalImportDialog from "./PortalImportDialog";
import TdsRowMenu, { ROW_MENU_CSS } from "./TdsRowMenu";
import {
  tdsApi,
  TdsBlocker,
  TdsChallan,
  TdsMonth,
  TdsQuarter,
} from "../../api/tds";

// The Income-tax Act 2025 renumbers the salary statement, but every return
// filed today — including a late or revised one for an earlier quarter — still
// goes on 24Q.
const TDS_RETURN_FORM = "24Q";

const QUARTER_OPTIONS = [
  { value: "1", label: "Q1 — Apr to Jun" },
  { value: "2", label: "Q2 — Jul to Sep" },
  { value: "3", label: "Q3 — Oct to Dec" },
  { value: "4", label: "Q4 — Jan to Mar" },
];

// Why a month cannot go into the return yet. The backend owns the rule and
// sends a code; the wording lives here.
const BLOCKER_LABEL: Record<TdsBlocker, string> = {
  payroll_not_approved: "Payroll not approved",
  challan_not_paid: "Challan not paid",
  challan_reference_missing: "BSR code / serial missing",
  pan_missing: "PAN missing",
};

const monthShort = (m: number) =>
  new Date(2000, m - 1, 1).toLocaleString("en", { month: "short" });

const fyLabel = (year: number, month: number) => {
  const start = month >= 4 ? year : year - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
};

const quarterForMonth = (m: number) =>
  m >= 4 && m <= 6 ? 1 : m >= 7 && m <= 9 ? 2 : m >= 10 ? 3 : 4;

const formatDay = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

const today = () => new Date().toISOString().slice(0, 10);

// --------------------------------------------------------------------------
// Pieces
// --------------------------------------------------------------------------

type StepState = "done" | "active" | "todo";

/** A statutory deadline. The colour carries the meaning, so it stays. */
function DueChip({ date, done }: { date: string; done?: boolean }) {
  if (done) return null;
  const days = Math.ceil(
    (new Date(date).getTime() - new Date(today()).getTime()) / 86_400_000,
  );
  const tone =
    days < 0
      ? { bg: "#fee2e2", fg: "#991b1b" }        // overdue
      : days <= 7
        ? { bg: "#fef3c7", fg: "#92400e" }      // due soon
        : { bg: "var(--gt-surface-2)", fg: "var(--gt-text-2, #64748b)" };
  return (
    <span
      style={{
        background: tone.bg,
        color: tone.fg,
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      Due {formatDay(date)}
    </span>
  );
}

/** The short "where does this stand" line under a step's name. */
function StepNote({ children, tone }: { children: React.ReactNode; tone?: "warn" }) {
  return (
    <p
      className="gt-page-sub"
      style={{ fontSize: 12, margin: "2px 0 0", color: tone === "warn" ? "#92400e" : undefined }}
    >
      {children}
    </p>
  );
}

interface StepDef {
  title: string;
  state: StepState;
  badge?: React.ReactNode;
  node?: React.ReactNode;
}

// The whole job as ONE numbered run, 1 through 7. Splitting it into a monthly
// flow and a quarterly flow made it read as two unrelated things that both
// started at step 1; in practice a person works straight through — settle each
// month's challan, then file the quarter's statement.
// The sections TDS is being built out into. Listed in the order the work
// runs, not alphabetically: deductions happen, then a challan is paid, then
// a return is filed, then certificates come back from TRACES.
//
// Several have nothing behind them yet. They are listed anyway, saying so
// plainly: a tab that is missing reads as "this will never exist", and a
// tab that is there but empty reads as broken. One that names what is
// coming reads as what it is.
type TdsSection =
  | "transactions" | "deductees" | "challan" | "challanData"
  | "returns" | "forms" | "reconciliation" | "reports";

const TDS_SECTIONS: { id: TdsSection; name: string; soon?: boolean }[] = [
  { id: "transactions", name: "Transactions" },
  { id: "deductees", name: "Deductees / Parties" },
  { id: "challan", name: "Challan & Payment" },
  { id: "challanData", name: "Challan Data" },
  { id: "returns", name: "Returns" },
  // Form 16, not "Form 16 / 16A": 16A is the certificate for NON-salary
  // deductions (26Q), and Teamlio files 24Q. Naming it here promised a
  // thing this module does not do.
  { id: "forms", name: "Form 16", soon: true },
  { id: "reconciliation", name: "TDS Reconciliation", soon: true },
  { id: "reports", name: "Reports", soon: true },
];

function SectionStrip({ value, onChange }: {
  value: TdsSection;
  onChange: (id: TdsSection) => void;
}) {
  return (
    <nav style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid var(--gt-border)" }}>
      {TDS_SECTIONS.map((section) => {
        const active = section.id === value;
        return (
          <button key={section.id} type="button" onClick={() => onChange(section.id)}
            style={{ padding: "8px 12px", fontSize: 12.5, fontWeight: 600, marginBottom: -1,
              borderBottom: active ? "2px solid var(--gt-primary)" : "2px solid transparent",
              color: active ? "var(--gt-primary-deep)" : "var(--gt-text-tertiary)" }}>
            {section.name}
          </button>
        );
      })}
    </nav>
  );
}

/** A section that is named but not built. Says what will be in it, so the
 *  tab is a promise rather than a dead end. */
// A section that is not built. Centred, and saying so in as many words
// rather than leaving somebody to work it out from an empty card: a blank
// panel reads as something that failed to load, and "Coming soon" never
// does.
function ComingSoon({ name }: { name: string }) {
  return (
    <div
      className="gt-card p-5"
      style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        textAlign: "center", minHeight: 260, gap: 10,
      }}
    >
      <span className="gt-eyebrow">{name}</span>
      <p style={{
        margin: 0, fontSize: 19, fontWeight: 600,
        letterSpacing: "0.01em", color: "var(--gt-text-secondary)",
      }}>
        Coming soon
      </p>
    </div>
  );
}

function StepFlow({
  title,
  caption,
  steps,
  action,
}: {
  title: string;
  caption: string;
  steps: StepDef[];
  /** Beside the heading: the portal sign-in this flow files through. */
  action?: React.ReactNode;
}) {
  return (
    <div className="gt-card p-5">
      <style>{STEP_CSS}</style>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <span className="gt-eyebrow">{title}</span>
        {action}
      </div>
      <p className="gt-page-sub" style={{ fontSize: 12, margin: "4px 0 14px" }}>
        {caption}
      </p>
      {/* The same strip PF and ESI use, one tab over. Seven steps do not fit
          a desktop, so it scrolls rather than wrapping: a wrapped row broke
          "1 through 7" into two rows that read as two separate flows, which
          is the exact confusion this component was made to end. */}
      <StatutorySteps
        steps={steps.map((step, i) => ({
          key: `${i}-${step.title}`,
          title: step.title,
          // The strip's vocabulary: a TDS step is never "blocked", so its
          // three states map straight onto three of the four.
          state: (step.state === "active" ? "current"
            : step.state === "todo" ? "locked" : "done") as StepState2,
          badge: step.badge,
          // TdsTab keeps a step's state line and its actions in one node.
          note: step.node,
        }))}
      />
    </div>
  );
}

/** A month of the quarter, coloured by whether it could go into the return. */
function MonthChip({
  month,
  settled,
  blockers,
  active,
  onClick,
}: {
  month: number;
  settled: boolean;
  blockers?: TdsBlocker[];
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={
        settled
          ? `${monthShort(month)} is ready for the return`
          : `${monthShort(month)}: ${(blockers || []).map((b) => BLOCKER_LABEL[b]).join(", ")}`
      }
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: 999,
        cursor: onClick ? "pointer" : "default",
        background: settled ? "#dcfce7" : "#fef3c7",
        color: settled ? "#166534" : "#92400e",
        border: active ? "1px solid currentColor" : "1px solid transparent",
      }}
    >
      {monthShort(month)}
    </button>
  );
}

// A step's action. No border or background — underlined instead, which is what
// makes it read as clickable without putting a row of competing boxes inside a
// step. A class rather than an inline style because :hover, :focus-visible and
// :disabled cannot be expressed inline, and the hover is half of the "you can
// click this" signal. Scoped to this page by its name; the app's own stylesheet
// has no link-style button to reuse.
// The message, and on a live build the Web Store tab, now come from
// utils/extensionInstall — the same handling the PF side uses, because "the
// extension did not answer" means the same thing on both.

// What a dry run sends as the deposit. Non-zero because the portal will not
// advance past an empty amount; small enough that it can never be mistaken for
// a real month's TDS if someone does carry it through.

const STEP_LINK = "tds-step-link";
const STEP_LINK_CSS = `
.tds-step-link {
  display: inline;
  background: none;
  border: 0;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
  color: var(--gt-primary);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  white-space: nowrap;
}
.tds-step-link:hover,
.tds-step-link:focus-visible {
  text-decoration-thickness: 2px;
}
.tds-step-link:disabled,
.tds-step-link[aria-disabled="true"] {
  opacity: 0.5;
  cursor: default;
  text-decoration: none;
}
.tds-pay-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.tds-pay-btn:disabled:hover {
  background: var(--gt-primary);
  border-color: var(--gt-primary);
  transform: none;
  box-shadow: none;
}
`;

// --------------------------------------------------------------------------
// Dialogs
// --------------------------------------------------------------------------

function RecordChallanDialog({
  challan,
  onClose,
  onSaved,
}: {
  challan: TdsChallan | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [bsr, setBsr] = useState("");
  const [serial, setSerial] = useState("");
  const [date, setDate] = useState(today());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!challan) return;
    setBsr(challan.bsr_code || "");
    setSerial(challan.challan_serial_no || "");
    setDate(challan.deposit_date || today());
  }, [challan]);

  const save = async () => {
    if (!challan) return;
    setSaving(true);
    try {
      await tdsApi.updateChallan(challan.id, {
        bsr_code: bsr.trim(),
        challan_serial_no: serial.trim(),
        deposit_date: date,
      });
      toast.success("Challan reference saved");
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not save the challan reference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!challan} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record challan reference</DialogTitle>
          <DialogDescription>
            The BSR code and serial number are printed on the payment receipt. The
            extension fills these in automatically when it can read the receipt —
            this is the fallback for when it could not, and the way a wrong value
            gets corrected. A {TDS_RETURN_FORM} cannot be filed without them.
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: "grid", gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            BSR code
            <input
              className="gt-input"
              value={bsr}
              onChange={(e) => setBsr(e.target.value)}
              placeholder="7 digits, e.g. 0510002"
              style={{ width: "100%", marginTop: 4 }}
            />
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Challan serial number
            <input
              className="gt-input"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="e.g. 61755"
              style={{ width: "100%", marginTop: 4 }}
            />
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Deposit date
            <input
              type="date"
              className="gt-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ width: "100%", marginTop: 4 }}
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !bsr.trim() || !serial.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkFiledDialog({
  open,
  financialYear,
  quarter,
  onClose,
  onSaved,
}: {
  open: boolean;
  financialYear: string;
  quarter: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [filedDate, setFiledDate] = useState(today());
  const [rrr, setRrr] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await tdsApi.markReturnFiled({
        financial_year: financialYear,
        quarter,
        filed_date: filedDate,
        acknowledgement_no: rrr.trim() || undefined,
      });
      toast.success(`${TDS_RETURN_FORM} marked as filed`);
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not record the return");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Mark {TDS_RETURN_FORM} filed — {financialYear} Q{quarter}
          </DialogTitle>
          <DialogDescription>
            Record the statement you uploaded on the e-filing portal. The RRR
            (acknowledgement) number is on the confirmation the portal shows after
            the upload is verified.
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: "grid", gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Filed on
            <input
              type="date"
              className="gt-input"
              value={filedDate}
              onChange={(e) => setFiledDate(e.target.value)}
              style={{ width: "100%", marginTop: 4 }}
            />
          </label>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Acknowledgement (RRR) number
            <input
              className="gt-input"
              value={rrr}
              onChange={(e) => setRrr(e.target.value)}
              placeholder="Optional — add it once the portal shows it"
              style={{ width: "100%", marginTop: 4 }}
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Mark filed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// The tab
// --------------------------------------------------------------------------

export default function TdsTab({ filters }: { filters: PayrollFilters }) {
  const now = new Date();
  const seedYear = filters.year || now.getFullYear();
  const seedMonth = filters.month || now.getMonth() + 1;

  const [financialYear, setFinancialYear] = useState(
    filters.financial_year || fyLabel(seedYear, seedMonth),
  );
  const [quarter, setQuarter] = useState(quarterForMonth(seedMonth));
  const [activeMonth, setActiveMonth] = useState<number | null>(null);

  const [data, setData] = useState<TdsQuarter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Narrows the lists below without re-reading: the quarter is already in
  // hand, so month and section are a view over it. Separate from the month
  // the steps work on -- that one asks "which month am I settling", this
  // one asks "which rows am I looking at".
  const [scope, setScope] = useState<TdsFilters>(EMPTY_FILTERS);

  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState<TdsChallan | null>(null);
  const [markingFiled, setMarkingFiled] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [creatingChallan, setCreatingChallan] = useState(false);
  const [section, setSection] = useState<TdsSection>("transactions");
  // const [debugLines, setDebugLines] = useState<ExtensionLogLine[] | null>(null);

  // What the extension last said, and whether it is still going.
  //
  // The portal runs in a window of its own and takes minutes. Without this
  // the page sits there looking idle while the run is either working, or
  // waiting for the person on a window they have not noticed -- and those
  // two look identical from here.
  const [portalStatus, setPortalStatus] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  // The challan import: whether one is running, and whether its progress
  // dialog has been put away. The run is in another window, so the dialog
  // being hidden does not stop it.
  const [readingPortal, setReadingPortal] = useState(false);
  const [importHidden, setImportHidden] = useState(false);
  // Bumped when an import lands, so Challan Data re-reads itself rather
  // than polling for a run it cannot see.
  const [portalKey, setPortalKey] = useState(0);

  useEffect(() => onItStatus((step, message) => {
    setPortalStatus(message);
    setPortalBusy(step !== "done" && step !== "error");
  }), []);
  useEffect(() => onItReady((message) => {
    setPortalStatus(message);
    setPortalBusy(false);
  }), []);
  useEffect(() => onItError((error) => {
    setPortalStatus(error);
    setPortalBusy(false);
    // A failed run used to leave the Import button spinning for ever:
    // nothing else ever cleared this, because the thing that would have
    // is the success path.
    setReadingPortal(false);
  }), []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    tdsApi
      .quarter(financialYear, quarter, scope.returnType)
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        // Land on the first month that still needs attention, so the page opens
        // on the work rather than on a month that is already done.
        setActiveMonth((current) => {
          const months = r.data.months.map((m) => m.month);
          if (current && months.includes(current)) return current;
          return (r.data.months.find((m) => !m.settled) || r.data.months[0])?.month ?? null;
        });
      })
      .catch((e: any) => {
        if (!alive) return;
        setData(null);
        setError(
          e?.response?.data?.detail || "Couldn't load TDS for this quarter. Please try again.",
        );
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [financialYear, quarter, scope.returnType, reloadKey]);

  const fyOptions = useMemo(() => {
    const base = Number(fyLabel(now.getFullYear(), now.getMonth() + 1).split("-")[0]);
    return [0, 1, 2].map((back) => {
      const start = base - back;
      const label = `${start}-${String(start + 1).slice(-2)}`;
      return { value: label, label };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const month: TdsMonth | undefined = useMemo(
    () => data?.months.find((m) => m.month === activeMonth),
    [data, activeMonth],
  );

  useEffect(() => onItHistoryDone((result) => {
    setReadingPortal(false);
    setPortalKey((k) => k + 1);
    const saved = result.saved || 0;
    const recorded = result.recorded || 0;
    if (!saved) {
      toast.success("The portal had no salary challans for this period.");
      return;
    }
    // What got written is the useful half: the rest need a decision, and
    // saying so is what sends somebody to Challan Data rather than
    // wondering whether the import did anything.
    toast.success(
      `${saved} salary challan${saved === 1 ? "" : "s"} imported`
      + (recorded
        ? ` — ${recorded} matched and marked paid.`
        : " — none matched a challan here; open Challan Data to record them."),
    );
    // A recorded receipt changes the challan list and the stepper.
    if (recorded) reload();
    // `reload` is a useCallback with no deps, so listing it costs nothing
    // and keeps the rule able to see that this closure is current.
  }), [reload]);

  // ---- actions ----------------------------------------------------------

  // The e-filing sign-in on its own. Same shape as EPFO's and ESIC's: when
  // a filing will not get past the portal, the first thing to find out is
  // whether the saved credentials still work -- and that answer should not
  // cost a walk through e-Pay Tax.
  const onPortalLogin = async () => {
    if (!(await pingItExtension())) {
      showExtensionMissing();
      return;
    }
    setLoggingIn(true);
    try {
      const { data: creds } = await incomeTaxSettingsApi.getCredentials();
      await startItLogin({
        username: creds.username,
        password: creds.password,
        authToken: sessionGet("accessToken") || undefined,
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
      });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(
        e?.response?.status === 404
          ? "Set the e-filing login first under Settings → Organization → Tax Settings."
          : detail || e?.message || "Could not open the e-filing portal",
      );
    } finally {
      setLoggingIn(false);
    }
  };

  // What the portal says was actually paid, as against what Teamlio thinks.
  // The only thing that settles a challan: a status here says what was
  // meant to happen, a BSR code on the portal says it happened.
  const onReadPortalChallans = async () => {
    if (!(await pingItExtension())) {
      showExtensionMissing();
      return;
    }
    // Cleared first: the dialog opens on this line, and the last run's
    // last words under a fresh spinner read as this run's progress.
    setPortalStatus("");
    setImportHidden(false);
    setReadingPortal(true);
    try {
      const { data: creds } = await incomeTaxSettingsApi.getCredentials();
      await startReadPortalChallans({
        username: creds.username,
        password: creds.password,
        authToken: sessionGet("accessToken") || undefined,
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
        taxYear: financialYear,
        quarter,
      });
      // Left running on purpose: the flag is cleared by onItHistoryDone or
      // onItError, minutes from now, not by this call returning.
      return;
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(
        e?.response?.status === 404
          ? "Set the e-filing login first under Settings → Organization → Tax Settings."
          : detail || e?.message || "Could not read the portal's challans",
      );
      setReadingPortal(false);
    }
  };

  const removeChallan = async (c: TdsChallan) => {
    // No confirm. Delete is already two clicks behind a menu, and a browser
    // dialog over a list of near-identical draft rows asks a question
    // nobody can answer from the words in it -- the row they meant is the
    // row they opened the menu on.
    //
    // The toast names what went instead, which is the useful half of a
    // confirmation and arrives without standing in the way.
    try {
      await tdsApi.deleteChallan(c.id);
      toast.success(`Deleted the ${monthShort(c.month)} ${c.section} challan — ${inr(c.tds_amount)}`);
      reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not delete that challan.");
    }
  };

  // // A refresh that does not shout. onShowDebugLog toasts when the extension
  // // cannot answer, which is right for a button press and wrong once a second.
  // const refreshDebugLog = useCallback(async () => {
    // try {
      // setDebugLines(await getItDebugLog());
    // } catch {
      // // Silent on purpose: this runs on a timer while the dialog is open,
      // // and a toast once a second over a portal that went away is worse
      // // than a log that stops growing.
    // }
  // }, []);

  // Hand a challan that already EXISTS to the extension.
  //
  // Called by Create Challan's own "Create & Pay on Portal", which has
  // just made the row -- so this takes its id rather than making another.
  // The old handler created a second challan on top of the dialog's,
  // because it was written for a button that had no challan to start from.
  const payChallanOnPortal = async (challan: TdsChallan) => {
    if (!(await pingItExtension())) {
      showExtensionMissing();
      return;
    }
    try {
      const { data: creds } = await incomeTaxSettingsApi.getCredentials();
      await startFileTds({
        username: creds.username,
        password: creds.password,
        authToken: sessionGet("accessToken") || undefined,
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
        taxYear: financialYear,
        section: challan.section || "192",
        // Every head, so the portal's form ends up carrying what the
        // challan says. Sending four of eight was how a challan with
        // surcharge or cess on it reached the portal short.
        amounts: {
          tdsAmount: challan.tds_amount,
          surchargeAmount: challan.surcharge_amount,
          eduCessAmount: challan.edu_cess_amount,
          interestAmount: challan.interest_amount,
          lateFeeAmount: challan.late_fee_amount,
          penaltyAmount: challan.penalty_amount,
          otherAmount: challan.other_amount,
        },
        challanId: challan.id,
      });
      reload();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(
        e?.response?.status === 404
          ? "Set the Income Tax e-filing login first under Settings → Organization → Income Tax Settings."
          : detail || e?.message || "Could not open the portal for that challan",
      );
    }
  };

  // const onShowDebugLog = async () => {
    // try {
      // const lines = await getItDebugLog();
      // setDebugLines(lines);
    // } catch (e: any) {
      // // A timeout means nothing answered. Anything else means something DID
      // // answer but does not handle getDebugLog -- so name the build that
      // // answered, because the usual cause is the browser running a Web Store
      // // copy while the edits go into the local folder, and reloading that
      // // copy will never help.
      // if (e?.message === "extension_timeout") {
        // showExtensionMissing();
        // return;
      // }
      // const version = await getItExtensionVersion();
      // toast.error(
        // `Extension v${version || "?"} answered but has no debug log. In chrome://extensions, ` +
          // 'check that "Teamlio PF Filing" is Loaded unpacked from the server/teamlio extension ' +
          // "folder — a Web Store copy never picks up local edits.",
      // );
    // }
  // };

  const onUploadData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Let the same file be picked twice in a row (a re-validated .fvu keeps its
    // name), which an unreset input would swallow.
    event.target.value = "";
    if (!file) return;

    if (!(await pingItExtension())) {
      showExtensionMissing();
      return;
    }

    setUploading(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      // In chunks: String.fromCharCode spreads its argument onto the stack, so
      // one call over a whole file blows the argument limit on anything but a
      // tiny statement.
      let binary = "";
      for (let i = 0; i < bytes.length; i += 8192) {
        binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
      }

      const { data: creds } = await incomeTaxSettingsApi.getCredentials();
      await startUpload24Q({
        username: creds.username,
        password: creds.password,
        authToken: sessionGet("accessToken") || undefined,
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
        form: TDS_RETURN_FORM,
        fileName: file.name,
        dataBase64: btoa(binary),
      });
      toast.info("Opening the portal — the statement will be attached for you to check and e-Verify.");
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(
        e?.response?.status === 404
          ? "Set the Income Tax e-filing login first under Settings → Organization → Income Tax Settings."
          : detail || e?.message || "Could not start the upload",
      );
    } finally {
      setUploading(false);
    }
  };

  const onDownloadData = async () => {
    setExporting(true);
    try {
      const { data: payload } = await tdsApi.returnData(financialYear, quarter);
      if (!payload.challans.length) {
        toast.error("No challans in this quarter yet.");
        return;
      }

      // Two sheets, the shape a 24Q is built from: the challans, and the
      // deductee breakup of each. Incomplete rows are included deliberately —
      // an export that dropped a challan with no BSR code would hide exactly
      // the problem you need to see before filing.
      const challanRows = payload.challans.map(({ challan }) => ({
        Month: monthShort(challan.month),
        Section: challan.section,
        "BSR Code": challan.bsr_code || "",
        "Challan Serial No": challan.challan_serial_no || "",
        "Deposit Date": challan.deposit_date || "",
        "TDS Amount": challan.tds_amount,
        Interest: challan.interest_amount,
        "Late Fee": challan.late_fee_amount,
        Other: challan.other_amount,
        CIN: challan.cin || "",
        CRN: challan.crn || "",
        Status: challan.status,
      }));

      const deducteeRows = payload.challans.flatMap(({ challan, deductees }) =>
        deductees.map((d) => ({
          Month: monthShort(challan.month),
          "BSR Code": challan.bsr_code || "",
          "Challan Serial No": challan.challan_serial_no || "",
          Employee: d.employee_name || "",
          PAN: d.pan || "",
          "Amount Paid": d.gross_amount,
          "TDS Deducted": d.tds_amount,
          Section: challan.section,
        })),
      );

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(challanRows), "Challans");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(deducteeRows), "Deductees");
      XLSX.writeFile(wb, `${TDS_RETURN_FORM}_${financialYear}_Q${quarter}.xlsx`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not build the return data");
    } finally {
      setExporting(false);
    }
  };

  // ---- render -----------------------------------------------------------

  // The challans the bar is showing. A challan with no month covers the
  // whole quarter -- one deposit for July, August and September -- so it
  // belongs under any month of it rather than under none.
  const visibleChallans = useMemo(() => {
    const every = (data?.months || []).flatMap((m) => m.challans);
    return every.filter((c) =>
      (!scope.month || c.month === null || c.month === scope.month)
      && (!scope.section || (c.section || "") === scope.section));
  }, [data, scope]);

  if (loading) return <Notice>Loading…</Notice>;
  if (error) return <Notice error>{error}</Notice>;
  if (!data) return <Notice>No TDS data for this quarter.</Notice>;

  const monthChallan = month?.challans[0] || null;
  const filed = data.return;
  const verified = !!filed && /verified|accepted|processed/i.test(filed.status || "");

  const monthlySteps: StepDef[] = month
    ? [
        {
          title: "Payroll Approved",
          state: month.not_approved?.records
            ? "active"
            : month.deductee_count || month.tds_total
              ? "done"
              : "todo",
          node: month.not_approved?.records ? (
            <StepNote tone="warn">
              {month.not_approved.records} salary record
              {month.not_approved.records > 1 ? "s" : ""} ({inr(month.not_approved.net)}) not
              approved —{" "}
              {/* The month may already have a figure from imported entries.
                  Saying "counted from approved payroll only" there is simply
                  untrue, and it talks somebody out of a challan they ought
                  to raise. */}
              {month.tds_total
                ? `the ${inr(month.tds_total)} below comes from the imported entries; approving these may add to it.`
                : "TDS is counted from approved payroll only, so paying now would under-deposit."}
            </StepNote>
          ) : month.deductee_count ? (
            <StepNote>
              {month.deductee_count} employee{month.deductee_count > 1 ? "s" : ""},{" "}
              {inr(month.tds_total)} TDS
              {month.missing_pan.length
                ? ` — ${month.missing_pan.length} without a PAN`
                : ""}
            </StepNote>
          ) : (
            <StepNote>No TDS deducted this month.</StepNote>
          ),
        },
        {
          title: "Pay Challan",
          badge: month.challans.length ? null : (
            <DueChip date={month.due_date} done={month.tds_total === 0} />
          ),
          state: month.challans.length ? "done" : month.tds_total ? "active" : "todo",
          node: (
            <>
              {month.challans.length ? (
                <StepNote>
                  {month.challans.length} challan{month.challans.length > 1 ? "s" : ""},{" "}
                  {inr(month.deposited)}
                </StepNote>
              ) : month.tds_total ? null : (
                <StepNote>Nothing to deposit.</StepNote>
              )}
              {/* One way to raise a challan, and it is the form that can
                  set the section, the deposit date and the interest. A
                  second button that made one from the month alone was a
                  shortcut past all three. */}
              <button
                type="button"
                className={STEP_LINK}
                onClick={() => { setSection("challan"); setCreatingChallan(true); }}
              >
                {month.challans.length ? "Pay another" : "Create challan"}
              </button>
            </>
          ),
        },
        {
          title: "Record BSR & Serial",
          state: month.blockers.includes("challan_reference_missing")
            ? "active"
            : month.challans.length
              ? "done"
              : "todo",
          node: monthChallan?.bsr_code && monthChallan?.challan_serial_no ? (
            <StepNote>
              BSR {monthChallan.bsr_code} · Serial {monthChallan.challan_serial_no}
              {monthChallan.deposit_date ? ` · ${formatDay(monthChallan.deposit_date)}` : ""}
            </StepNote>
          ) : monthChallan ? (
            <>
              <StepNote tone="warn">
                Filled automatically once the extension reads the payment receipt.
              </StepNote>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button type="button" className={STEP_LINK} onClick={() => setRecording(monthChallan)}>
                  Enter manually
                </button>
                {/* The other way to fill this in: read it off the portal
                    rather than copy it across by hand. Offered here because
                    this is the step that is waiting for it. */}
                <button type="button" className={STEP_LINK}
                  disabled={readingPortal}
                  title="Sign in to the e-filing portal, read its payment history and fill in the BSR code and serial for challans paid there."
                  onClick={onReadPortalChallans}>
                  {readingPortal ? "Importing…" : "Import challan data"}
                </button>
              </div>
            </>
          ) : (
            <StepNote>Pay the challan first.</StepNote>
          ),
        },
      ]
    : [];

  // The month picker leads the run. Every monthly step below acts on the
  // month chosen here, so choosing it is step 1 rather than a review that
  // sits after the work it governs.
  const monthPickerStep: StepDef = {
    title: "Months Settled",
    state: data.quarter_ready ? "done" : "active",
    node: (
      <>
        <div style={{ display: "flex", gap: 6, marginTop: 4, justifyContent: "center", flexWrap: "wrap" }}>
          {data.months.map((m) => (
            <MonthChip
              key={m.month}
              month={m.month}
              settled={m.settled}
              blockers={m.blockers}
              active={m.month === activeMonth}
              onClick={() => setActiveMonth(m.month)}
            />
          ))}
        </div>
        {!data.quarter_ready && (
          <StepNote tone="warn">
            {data.months
              .filter((m) => !m.settled)
              .map(
                (m) =>
                  `${monthShort(m.month)}: ${m.blockers
                    .map((b) => BLOCKER_LABEL[b])
                    .join(", ")}`,
              )
              .join(" · ")}
          </StepNote>
        )}
      </>
    ),
  };

  const quarterlySteps: StepDef[] = [
    {
      title: "Validate (FVU)",
      state: data.quarter_ready ? "active" : "todo",
      node: (
        <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "center", flexWrap: "nowrap", marginTop: 2 }}>
          <button type="button" className={STEP_LINK} onClick={onDownloadData} disabled={exporting}>
            {exporting ? "Building…" : "Download Data"}
          </button>
          <label className={STEP_LINK} aria-disabled={uploading}>
            {uploading ? "Opening…" : "Upload Data"}
            <input
              type="file"
              accept=".fvu"
              hidden
              disabled={uploading}
              onChange={onUploadData}
            />
          </label>
        </div>
      ),
    },
    {
      title: `File ${TDS_RETURN_FORM}`,
      badge: filed ? null : <DueChip date={data.return_due_date} />,
      state: verified ? "done" : filed || data.quarter_ready ? "active" : "todo",
      node: filed ? (
        <StepNote>
          Filed {formatDay(filed.filed_date)}
          {filed.acknowledgement_no ? ` · Ack ${filed.acknowledgement_no}` : ""}
        </StepNote>
      ) : (
        <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "center", flexWrap: "nowrap", marginTop: 2 }}>
          <a
            href="https://eportal.incometax.gov.in/"
            target="_blank"
            rel="noreferrer"
            className={STEP_LINK}
          >
            File TDS
          </a>
          <button type="button" className={STEP_LINK} onClick={() => setMarkingFiled(true)}>
            Mark as Filed
          </button>
        </div>
      ),
    },
    {
      title: "Issue Form 16",
      state: "todo",
      node: <StepNote>Annual, due 15 June. Not built yet.</StepNote>,
    },
  ];

  // One run, numbered straight through. The groups stay separate above because
  // each is built from its own state; StepFlow numbers whatever it is handed,
  // so this order is the only thing that decides the numbering.
  const allSteps: StepDef[] = [monthPickerStep, ...monthlySteps, ...quarterlySteps];

  return (
    <div className="space-y-4">
      <style>{STEP_LINK_CSS}{ROW_MENU_CSS}</style>
      <TdsFilterBar
        financialYear={financialYear}
        fyOptions={fyOptions}
        onFinancialYear={setFinancialYear}
        quarter={quarter}
        quarterOptions={QUARTER_OPTIONS}
        onQuarter={setQuarter}
        filters={scope}
        onFilters={setScope}
        onApply={reload}
        refreshing={loading}
      />

      {/* What the extension is doing, on every sub-tab rather than buried in
          one section's footer. A portal run happens in another window and
          takes minutes; this strip and the log behind it are the only place
          it is visible from here. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "0 2px" }}>
        <span style={{ fontSize: 11.5, color: "var(--gt-text-secondary)", flex: 1, minWidth: 220 }}>
          {portalStatus
            ? <>
                {portalBusy && (
                  <span aria-hidden style={{ marginRight: 6 }}>⏳</span>
                )}
                <strong style={{ fontWeight: 600 }}>Portal:</strong> {portalStatus}
              </>
            : <span style={{ color: "var(--gt-text-tertiary)" }}>
                The extension has not run yet this session.
              </span>}
        </span>
      </div>

      <StepFlow
        title={`TDS — ${financialYear} Q${quarter}${month ? ` · ${monthShort(month.month)} ${month.year}` : ""}`}
        caption={
          `Step 1 picks the month. Steps 2–${monthlySteps.length + 1} run once for that month; steps ` +
          `${monthlySteps.length + 2}–${allSteps.length} run once for the quarter. TDS is deposited by the ` +
          `7th of the following month (March: 30 April).`
        }
        steps={allSteps}
        action={
          // The e-filing sign-in, with the filing it serves. EPFO and ESIC
          // each have theirs in their own card; a TDS run that cannot get
          // past the portal is answered the same way -- find out whether
          // the saved login still works, without starting a filing to do it.
          <button type="button" className="gt-portal-btn"
            disabled={loggingIn}
            title="Open the Income Tax e-filing portal and sign in with the saved credentials. You solve the CAPTCHA; nothing is filed."
            onClick={onPortalLogin}>
            {loggingIn ? "Opening portal\u2026" : "Login to portal"}
          </button>
        }
      />
      

      <SectionStrip value={section} onChange={setSection} />

      {section === "transactions" && (
        <TdsTransactions financialYear={financialYear} quarter={quarter} filters={scope} />
      )}

      {section === "deductees" && (
        <TdsDeductees financialYear={financialYear} quarter={quarter} filters={scope} />
      )}

      {section === "challan" && (
        <>
        <div className="gt-card p-5" style={{ display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <span className="gt-eyebrow">Challan</span>
            <p className="gt-page-sub" style={{ fontSize: 12, margin: "4px 0 0", maxWidth: 560 }}>
              Every challan created or paid for {financialYear} Q{quarter}. New challans
              are created for the month picked in the cycle above.
            </p>
          </div>
          <button type="button" className="gt-action-btn" onClick={() => setCreatingChallan(true)}>
            Create Challan
          </button>
        </div>
        <div className="gt-card p-0">
          <div style={{ overflow: "auto" }}>
            <table style={{ width: "100%", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--gt-surface-2)", textAlign: "right" }}>
                  {["Month", "Section", "BSR Code", "Serial No", "Deposit Date"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: 8 }}>
                      {h}
                    </th>
                  ))}
                  <th style={{ padding: 8 }}>TDS</th>
                  <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                  <th style={{ padding: 8, width: 44 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleChallans.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: 16, textAlign: "center", color: "var(--gt-text-2, #94a3b8)" }}>
                      {data.months.flatMap((m) => m.challans).length
                        ? "No challans match the month and section above."
                        : "No challans in this quarter yet."}
                    </td>
                  </tr>
                )}
                {visibleChallans.map((c) => (
                    <tr key={c.id} style={{ borderTop: "1px solid var(--gt-border)", textAlign: "right" }}>
                      <td style={{ textAlign: "left", padding: 8 }}>{monthShort(c.month)}</td>
                      <td style={{ textAlign: "left", padding: 8 }}>{c.section}</td>
                      <td style={{ textAlign: "left", padding: 8 }}>{c.bsr_code || "—"}</td>
                      <td style={{ textAlign: "left", padding: 8 }}>{c.challan_serial_no || "—"}</td>
                      <td style={{ textAlign: "left", padding: 8 }}>{formatDay(c.deposit_date) || "—"}</td>
                      <td style={{ padding: 8 }}>{inr(c.tds_amount)}</td>
                      <td style={{ textAlign: "left", padding: 8 }}>
                        {c.status === "paid" ? "Paid" : "Draft"}
                      </td>
                      <td style={{ padding: 8 }}>
                        <TdsRowMenu items={[
                          { label: "Edit", onSelect: () => setRecording(c) },
                          {
                            label: "Delete",
                            danger: true,
                            onSelect: () => removeChallan(c),
                          },
                        ]} />
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* The action the old header button carried. It repeats step 2 on
              purpose -- this is the table someone lands on when they are
              chasing a challan -- and calls the same handler, so a challan
              can only ever be raised one way. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              padding: 12,
              borderTop: "1px solid var(--gt-border)",
            }}
          >
            <span style={{ fontSize: 12, color: "var(--gt-text-2, #94a3b8)" }}>
              {month
                ? `${monthShort(month.month)} ${month.year} — ${inr(month.tds_total)} TDS, ${inr(month.deposited)} deposited`
                : "Pick a month in step 1 to pay its challan."}
            </span>
          </div>
        </div>
        </>
      )}

      {section === "challanData" && (
        <TdsChallanData
          financialYear={financialYear}
          quarter={quarter}
          reloadKey={portalKey}
          importing={readingPortal}
          onImport={onReadPortalChallans}
        />
      )}

      {section === "returns" && (
        <ComingSoon name="Returns" />
      )}

      {TDS_SECTIONS.filter((x) => x.soon).map((x) => (
        section === x.id ? <ComingSoon key={x.id} name={x.name} /> : null
      ))}

      <PortalImportDialog
        open={readingPortal && !importHidden}
        status={portalStatus}
        onHide={() => setImportHidden(true)}
      />

      {/* Removed on request: nothing opens this any more. */}
      <RecordChallanDialog
        challan={recording}
        onClose={() => setRecording(null)}
        onSaved={reload}
      />
      {creatingChallan && (
        <CreateChallanDialog
          financialYear={financialYear}
          quarter={quarter}
          month={month ? month.month : null}
          onClose={() => setCreatingChallan(false)}
          onCreated={reload}
          onCreateAndPay={payChallanOnPortal}
        />
      )}
      <MarkFiledDialog
        open={markingFiled}
        financialYear={financialYear}
        quarter={quarter}
        onClose={() => setMarkingFiled(false)}
        onSaved={reload}
      />
    </div>
  );
}
